import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { fileUrl } from "@/components/MediaUploader";

export default function Lightbox({ items = [], index = 0, onClose }) {
  const [i, setI] = useState(index);
  const [loading, setLoading] = useState(true);
  const touch = useRef(null);

  const clamp = useCallback((n) => (items.length ? (n + items.length) % items.length : 0), [items.length]);
  const go = useCallback((delta) => { setLoading(true); setI((c) => clamp(c + delta)); }, [clamp]);

  useEffect(() => { setI(index); }, [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  if (!items.length) return null;
  const cur = items[i];

  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touch.current == null) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
    touch.current = null;
  };

  return (
    <div data-testid="lightbox" className="fixed inset-0 z-[100] bg-[#0e1014] flex flex-col select-none"
      onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Top bar — stile finestra */}
      <div className="flex items-center justify-between px-4 h-11 bg-[#1c1f26] border-b border-white/10 text-white/90" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-mono flex items-center gap-3 min-w-0">
          <span className="shrink-0">{i + 1} / {items.length}</span>
          <span className="text-white/55 truncate">{cur?.filename || ""}</span>
        </div>
        <div className="flex items-center gap-1">
          <a data-testid="lightbox-download" href={fileUrl(cur.file_id)} target="_blank" rel="noreferrer" download
            title="Scarica originale" className="w-8 h-8 rounded-[3px] flex items-center justify-center text-white/80 hover:bg-white/10"><Download size={17} /></a>
          <button data-testid="lightbox-close" title="Chiudi (Esc)" onClick={onClose}
            className="w-8 h-8 rounded-[3px] flex items-center justify-center text-white/80 hover:bg-[#e02434] hover:text-white transition-colors"><X size={20} /></button>
        </div>
      </div>

      {/* Image area con zone-hover ai bordi che rivelano le frecce */}
      <div className="flex-1 relative flex items-center justify-center overflow-hidden bg-[#0e1014]" onClick={(e) => e.stopPropagation()}>
        {items.length > 1 && (
          <div className="group absolute left-0 top-0 bottom-0 w-[22%] flex items-center justify-start pl-3 z-10 cursor-pointer"
            onClick={() => go(-1)} data-testid="lightbox-prev-zone">
            <button data-testid="lightbox-prev" className="p-2.5 rounded-full bg-white/10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 backdrop-blur-sm pointer-events-none"><ChevronLeft size={30} /></button>
          </div>
        )}
        {loading && <div className="absolute inset-0 flex items-center justify-center text-white/50 text-sm animate-pulse">Caricamento…</div>}
        <img key={cur.file_id} src={fileUrl(cur.file_id, 1400)} alt={cur.filename || ""}
          onLoad={() => setLoading(false)} onError={() => setLoading(false)}
          className="max-h-[80vh] max-w-[90vw] object-contain rounded-[2px] shadow-2xl" />
        {items.length > 1 && (
          <div className="group absolute right-0 top-0 bottom-0 w-[22%] flex items-center justify-end pr-3 z-10 cursor-pointer"
            onClick={() => go(1)} data-testid="lightbox-next-zone">
            <button data-testid="lightbox-next" className="p-2.5 rounded-full bg-white/10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-150 backdrop-blur-sm pointer-events-none"><ChevronRight size={30} /></button>
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {items.length > 1 && (
        <div className="flex gap-2 overflow-x-auto px-4 py-3 justify-center bg-[#1c1f26] border-t border-white/10" onClick={(e) => e.stopPropagation()}>
          {items.map((m, idx) => (
            <button key={m.file_id} data-testid={`lightbox-thumb-${idx}`} onClick={() => { setLoading(true); setI(idx); }}
              className={`shrink-0 rounded-[3px] overflow-hidden border-2 ${idx === i ? "border-white" : "border-transparent opacity-55 hover:opacity-100"}`}
              style={{ width: 54, height: 54 }}>
              <img src={fileUrl(m.file_id, 120)} alt="" loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
